<script setup lang="ts">
import { ref, computed, watch, onMounted, useTemplateRef } from "#imports";
import MPButton from "../forms/MPButton.vue";

const { slidesPerView } = defineProps<{ slidesPerView: number }>();

const slides = ref(
  Array.from({ length: slidesPerView }, (slide: null, idx: number) => idx),
);

const slideRefs = useTemplateRef("slides");
const order = ref([...slides.value]);
const middleSlide = computed(() => Math.floor(slidesPerView / 2));

const currentSlide = ref(middleSlide.value);
const updateSlideStyles = () => {
  slideRefs.value!.forEach((slide, idx) => {
    slide.style.zIndex = "1";
    slide.style.height = "240px";
    slide.style.opacity = "0.2";
    slide.style.marginTop = "2rem";
    slide.style.boxShadow = "var(--shadow-sm)";
    if (idx === currentSlide.value - 1) {
      slide.style.zIndex = "2";
      slide.style.height = "280px";
      slide.style.marginTop = "1.5rem";
      slide.style.opacity = "0.8";
      slide.style.boxShadow = "var(--shadow-md)";
    }
    if (idx === currentSlide.value + 1) {
      slide.style.zIndex = "2";
      slide.style.height = "280px";
      slide.style.marginTop = "1rem";
      slide.style.opacity = "0.9";
      slide.style.boxShadow = "var(--shadow-md)";
    }
    if (idx === currentSlide.value) {
      slide.style.zIndex = "3";
      slide.style.height = "320px";
      slide.style.opacity = "1";
      slide.style.marginTop = "0";
      slide.style.boxShadow = "var(--shadow-lg)";
    }
  });
};

const shuffleOrder = (direction: "prev" | "next") => {
  if (direction === "next") {
    order.value.push(order.value.shift()!);
  } else if (direction === "prev") {
    order.value.unshift(order.value.pop()!);
  }
};

const incrementCurrentSlide = () => {
  if (currentSlide.value >= 4) {
    currentSlide.value = 0;
  } else {
    currentSlide.value++;
  }
};

const decrementCurrentSlide = () => {
  if (currentSlide.value <= 0) {
    currentSlide.value = 4;
  } else {
    currentSlide.value--;
  }
};

const trackCurrentSlide = (direction: "prev" | "next") => {
  if (direction === "prev") {
    incrementCurrentSlide();
  } else if (direction === "next") {
    decrementCurrentSlide();
  }
};

const changeSlide = (direction: "prev" | "next") => {
  trackCurrentSlide(direction);
  shuffleOrder(direction);
  updateSlideStyles();
};

const carousel = useTemplateRef("carousel");
const carouselWidth = ref(0);
onMounted(() => {
  updateSlideStyles();
  carouselWidth.value = carousel.value!.clientWidth;
});

watch(currentSlide, () => {
  updateSlideStyles();
});
</script>

<template>
  <div class="card-carousel" ref="carousel">
    <div class="card-carousel-content">
      <div
        class="slide-wrapper"
        v-for="idx of slides"
        :key="idx"
        ref="slides"
        :style="{
          width: `${100 / slidesPerView}%`,
          transform: `translateX(calc(${(order[idx]! - middleSlide) * 100}% - ${((slidesPerView - 1) / 2) * 1}rem + ${order[idx]! * 1}rem))`,
        }"
      >
        <slot :name="`slide-${idx}`" />
      </div>
    </div>
    <div class="carousel-buttons">
      <MPButton
        icon="ri-arrow-left-s-line"
        version="secondary"
        backgroundColor="transparent"
        label="Previous"
        size="small"
        hideLabel
        rounded
        @click="changeSlide('next')"
      />
      <MPButton
        icon="ri-arrow-right-s-line"
        version="secondary"
        backgroundColor="transparent"
        label="Next"
        size="small"
        hideLabel
        rounded
        @click="changeSlide('prev')"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "assets/css/breakpoints.pcss";

.card-carousel {
  position: relative;
  display: flex;
  justify-content: center;
  height: 320px;
  width: 100%;
  margin-bottom: 4rem;
}

.card-carousel-content {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.slide-wrapper {
  position: absolute;
  width: calc(v-bind(100 / slidesPerView + "%"));
  min-width: 17rem;
  min-height: fit-content;
  top: 0;
  z-index: 1;
  border-radius: 1rem;
  transition: all 0.25s ease-in-out;
}

.carousel-buttons {
  display: flex;
  position: absolute;
  bottom: -4rem;
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;
  gap: 2rem;
}
</style>
